/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {getJobSearchAggregations, getJobSearchV3} from '@ion/core/src/services/Connect';
import {Device, GenericObject} from '@ion/core/src/types/entities';
import {dateFormatter} from '@ion/core/src/utils/DateUtil';

let cachedDateFormat: string | null = null;
let cachedTimeFormat: string | null = null;

export enum JobConflictStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  THROTTLED = 'THROTTLED',
  RETRY = 'RETRY'
};

export enum JoinFormat {
  COMMA = ',',
  OR = ' OR '
};

export type ConflictPaginatedRequest = {
  results: GenericObject[];
  totalCount: number;
}

/**
 * Retrieves the unique identifier for a given device.
 *
 * @param   {Device} device - The device object.
 * @returns {string} - The device identifier (serial number or ESN).
 */
const getDeviceIdentifier = (device: Device): string => (
  device?.category === 'MDT' ? device?.serialNumber : device?.esn
);

/**
 * Formats an array of devices into a query string, joining the device identifiers by the specified format.
 *
 * @param   {Device[]} devices - Array of device objects.
 * @param   {JoinFormat} [format=JoinFormat.OR] - The format used to join device identifiers.
 * @returns {string} - The formatted query string.
 */
export const formatDeviceQueryString = (devices: Device[], format: JoinFormat = JoinFormat.OR): string => {
  if (!isNil(devices)) {
    if (format === JoinFormat.COMMA) {
      return devices?.map?.(device => getDeviceIdentifier(device))?.join(format);
    }
    return devices?.map?.(device => `deviceIdentifier:${getDeviceIdentifier(device)}`)?.join(format);
  }
};

/**
 * Formats an array of job IDs into a query string, joining the job IDs by the specified format.
 *
 * @param   {number[]} jobIds - Array of job IDs.
 * @param   {JoinFormat} [format=JoinFormat.OR] - The format used to join job IDs.
 * @returns {string} - The formatted job ID query string.
 */
export const formatJobIdString = (jobIds: number[], format: JoinFormat = JoinFormat.OR): string => {
  if (!isNil(jobIds)) {
    return `${jobIds?.map?.(jobId => `jobId:${jobId}`)?.join(format)}`;
  }
};

/**
 * Retrieves the unique count of devices involved in conflicts from the given data set.
 *
 * @param   {Array} data - Array of task objects, each containing an array of jobs, and each 
 *                         job contains an array of devices.
 * @returns {Object} - The unique count of devices.
 */
export const getUniqueDeviceCount = (data) => {
  const uniqueDevices = new Set();
  data?.forEach((taskGroup) => {
    taskGroup?.forEach((job) => {
      if (Array.isArray(job.devices)) {
        job.devices.forEach((device) => {
          uniqueDevices.add(device.deviceIdentifier);
        });
      }
    });
  });

  return uniqueDevices.size;
};

/**
 * Extracts the job IDs from the aggregation data.
 *
 * @param   {Array} aggregations - Array of aggregation data.
 * @returns {number[]} - Array of job IDs.
 */
export const getJobIds = (aggregations) => {
  const jobIdTerms = aggregations?.find(item => item?.name === 'jobId-terms');
  return jobIdTerms ? jobIdTerms?.value?.buckets?.map?.(bucket => bucket?.key) : [];
};

/**
 * Checks if all devices are involved in conflicts.
 *
 * @param   {Device[]} devices - Array of device objects.
 * @param   {Array} conflicts - Array of task objects containing jobs and devices.
 * @returns {boolean} - True if all devices are in conflicts, false otherwise.
 */
export const areAllDevicesInConflicts = (devices, conflicts): boolean => {
  const conflictDeviceIdentifiers = new Set<string>();
  conflicts?.forEach?.(conflictGroup => {
    conflictGroup?.forEach?.(conflict => {
      if (conflict?.devices && Array.isArray(conflict.devices)) {
        conflict?.devices?.forEach?.(device => {
          if (device?.deviceIdentifier) {
            conflictDeviceIdentifiers.add(device.deviceIdentifier);
          }
        });
      }
    });
  });
  return devices.every(device => conflictDeviceIdentifiers.has(getDeviceIdentifier(device)));
};

/**
 * Returns a formatted string indicating how long ago the date was.
 *
 * @param   {string} createdOn - The creation date string.
 * @returns {string} - A string like "Today," "Yesterday," or "X days ago."
 */
export const getRelativeDate = (createdOn: string): string => {
  const createdDate = new Date(createdOn);
  const today       = new Date();
  const diffTime    = today.getTime() - createdDate.getTime();
  const diffDays    = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays === 0) {
    return translate('common:time.today');
  } else if (diffDays === 1) {
    return translate('common:time.yesterday');
  } else {
    return translate('common:time.daysAgoLabel', {days: diffDays});
  }
};

/**
 * Retrieves and caches the user's preferred date format.
 *
 * @returns {string} - The cached or fetched date format.
 */
const getCachedDateFormat = (): string => {
  if (!cachedDateFormat) {
    cachedDateFormat = UserPreferences.getSystemDateFormat();
  }
  return cachedDateFormat;
};

/**
 * Retrieves and caches the user's preferred time format.
 *
 * @returns {string} - The cached or fetched time format.
 */
const getCachedTimeFormat = (): string => {
  if (!cachedTimeFormat) {
    cachedTimeFormat = UserPreferences.getSystemTimeFormat();
  }
  return cachedTimeFormat;
};

/**
 * Formats the given date with both cached date and time formats.
 *
 * @param   {Date} date - The date to format.
 * @returns {string} - The formatted date and time string.
 */
export const formatDateTime = (date: Date): string => {
  return dateFormatter(date, `${getCachedDateFormat()}, ${getCachedTimeFormat()}`);
};

/**
 * Transforms the conflict result data into a format that can be used in the JobConflictResolutionChooser component.
 *
 * @param   {GenericObject[]} results - Array of result objects.
 * @param   {Object[]} jobDetails - Array of job details objects.
 * @param   {Object[]} tasks - Array of task objects.
 * @returns {Promise<GenericObject[]>} - The transformed results grouped by task and job.
 */
export const transformConflictResult = async(results: GenericObject[], jobDetails, tasks): Promise<GenericObject[]> => {
  const platformIdOrderMap = tasks.reduce((acc, task, index) => {
    acc[task.platformId] = index;
    return acc;
  }, {});

  const groupedConflictsByPlatformId = results.reduce((groupedResults, result) => {
    const {job, taskStatus, device, platformId} = result || {};
    const {id: jobId, taskIndex, creationDate}  = job || {};
    const {status}                              = taskStatus || {};
    const {esn, serialNumber, lastModifiedOn}   = device || {};

    const jobDetail   = jobDetails.find(job => job.jobId === jobId) || {};
    const taskDetails = tasks?.find(task => task.platformId === platformId) || {};
    const type        = `${taskDetails?.deviceType || ''} ${taskDetails?.fileType || ''}`.trim();

    const deviceObject = {
      deviceIdentifier : esn || serialNumber,
      task             : taskIndex,
      age              : getRelativeDate(creationDate),
      creator          : jobDetail?.username,
      jobName          : jobDetail?.name,
      progressStatus   : jobDetail?.progressStatus,
      jobStatus        : jobDetail?.status,
      updateTime       : formatDateTime(lastModifiedOn),
      taskStatus       : status,
      tasks            : jobDetail?.tasks || [],
      jobId,
      type
    };

    const groupKey = `${platformId}`;
    if (!groupedResults[groupKey]) {
      groupedResults[groupKey] = {};
    }
    if (!groupedResults[groupKey][jobId]) {
      groupedResults[groupKey][jobId] = {
        id      : jobId,
        task    : taskIndex,
        status  : status,
        devices : [deviceObject],
        platformId,
        type
      };
    } else {
      groupedResults[groupKey][jobId].devices.push(deviceObject);
    }
    return groupedResults;
  }, {});

  // We need to ensure that the conflicts are sorted by the order of the defined tasks
  const sortedResults = Object.keys(groupedConflictsByPlatformId).sort((a, b) => {
    const platformIdOrderA = platformIdOrderMap[a];
    const platformIdOrderB = platformIdOrderMap[b];
    return platformIdOrderA - platformIdOrderB;
  }).map((groupKey) => Object.values(groupedConflictsByPlatformId[groupKey]));

  return sortedResults;
};

/**
 * Filters job IDs to return only unique values.
 *
 * @param   {string[]} jobIds - Array of job ID strings.
 * @returns {string[]} - Array of unique job IDs.
 */
export const getUniqueJobIds = (jobIds: string[]): string[] => {
  return jobIds.filter((value, index, self) => self.indexOf(value) === index);
};

/**
 * Formats the transaction status array into a query string.
 *
 * @param   {string[]} transactionStatus - Array of transaction statuses.
 * @returns {string} - The formatted transaction status query string.
 */
export const formatTransactionStatus = (transactionStatus: string[]): string => {
  return transactionStatus?.map(status => `transactionStatus:${status}`)?.join(' OR ') || '';
};

/**
 * Extracts job IDs from the provided array of results.
 *
 * @param   {GenericObject[]} results - Array of result objects.
 * @returns {string[]} - Array of extracted job IDs.
 */
export const extractJobIdsFromDevices = (results: GenericObject[]): string[] => {
  const jobIds = results.map((result) => result?.job?.id).filter(Boolean);
  return getUniqueJobIds(jobIds);
};

/**
 * Fetches paginated job details for the given job IDs.
 *
 * @param   {string[]} jobIds - Array of job ID strings.
 * @param   {string} accountId - The account ID.
 * @returns {Promise<GenericObject[]>} - Array of job details.
 */
export const fetchPaginatedJobDetails = async(jobIds, accountId) => {
  let allJobDetails = [];
  let currentPage   = 1;
  let totalCount    = 0;
  const jobIdQueryString = jobIds.map(jobId => `jobId:${jobId}`).join(' OR ');
  do {
    try {
      const {results, totalCount: count} = await getJobSearchV3({
        accountId,
        query : `(${jobIdQueryString})`,
        page  : currentPage
      }) as { results: GenericObject[]; totalCount: number };
      totalCount = count;
      allJobDetails = [...allJobDetails, ...results];
      currentPage++;
    } catch (err) {
      error('Failed to fetch job details:', err);
      break;
    }
  } while (allJobDetails.length < totalCount);

  return allJobDetails;
};

/**
 * Fetches paginated job search results for the given parameters.
 *
 * The function performs an initial request to determine the total number of pages required.
 * It then iterates over the remaining pages, fetching all available results.
 * 
 * @param accountId - ID of the account to fetch data for.
 * @param devices - Query string representing the list of devices.
 * @param transactionStatus - Statuses to filter transactions by.
 * @param platformId - The platform identifier for job aggregation.
 * @param searchEntireAccount - Flag indicating if the entire account should be searched.
 * 
 * @returns - A promise that resolves to a list of Device conflict results.
 */
export const fetchPaginatedResults = async({
  accountId,
  devices,
  transactionStatus,
  platformId,
  searchEntireAccount
}) => {
  let allResults  = [];
  let currentPage = 1;
  let totalCount  = 0;
  let totalPages  = 0;
  const pageSize  = 100;
  try {
    const initialResponse = await getJobSearchAggregations({
      accountId,
      devices,
      transactionStatus,
      platformId,
      searchEntireAccount,
      page : currentPage,
      pageSize
    }) as ConflictPaginatedRequest;

    if (initialResponse?.results) {
      totalCount = initialResponse?.totalCount;
      allResults = [...allResults, ...initialResponse.results];
      totalPages = Math.ceil(totalCount / pageSize);
    }

    for (currentPage = 2; currentPage <= totalPages; currentPage++) {
      try {
        const {results} = await getJobSearchAggregations({
          accountId,
          devices,
          transactionStatus,
          platformId,
          searchEntireAccount,
          page : currentPage,
          pageSize
        }) as ConflictPaginatedRequest;
        if (results) {
          allResults = [...allResults, ...results];
        }
      } catch (err) {
        error(`Failed to fetch conflicts for page ${currentPage}:`, err);
        return allResults;
      }
    }
  } catch (err) {
    error('Failed to fetch Device conflicts:', err);
    return allResults;
  }

  return allResults;
};

/**
 * Parses the task list and returns a mapped array of task objects.
 *
 * @param   {Object} tasksList - The list of tasks.
 * @returns {Object[]} - Mapped array of tasks with platformId, deviceType, and fileType.
 */
export const parseTasks = (tasksList) => {
  const {tasks} = tasksList || {};
  return tasks?.map(taskWrapper => {
    const task = taskWrapper?.task;
    return {
      platformId : task.platformId,
      deviceType : task.deviceType,
      fileType   : task.fileType
    };
  });
};
